import {
  Box,
  Stack,
  Typography,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '@mui/material';
import { Card } from '@liminal/web-ui/primitives/Card';
import { styled } from '@mui/material/styles';
import { useUserWallets, useUserBalances } from '@/hooks/useUsers';
import { formatCurrencyAmount } from '@liminal/utils/currency';
import { formatDate } from '@liminal/utils/date';
import { truncateMiddle } from '@liminal/utils/truncate';
import { StatusPill } from '@liminal/web-ui/primitives/StatusPill';
import { CopyableId } from '@liminal/web-ui/display/CopyableId';
import { DataTableBody } from '@liminal/web-ui/display/DataTableBody';
import { DetailRow } from '@liminal/web-ui/display/DetailRow';
import { StatGrid } from '@liminal/web-ui/display/StatGrid';
import { EmptyState } from '@liminal/web-ui/feedback/EmptyState';
import { WarningCard } from '@liminal/web-ui/feedback/WarningCard';
import { Skeleton } from '@liminal/web-ui/feedback/Skeleton';
import { MonoCell } from '@/components/ui/MonoCell';
import { SortableTableCell } from '@liminal/web-ui/display/SortableTableCell';
import { useTableSort } from '@liminal/web-ui/primitives/useTableSort';
import type { AdminWallet } from '@/api/types';

/**
 * The user's whole wallet set arrives in one response, so this is a true sort.
 *
 * The per-asset Positions tables below are deliberately not sortable: each is
 * rendered inside a map over assets and holds a vault position or two, so a
 * header there would be chrome on a one-row table.
 */
const WALLET_SORT_ACCESSORS = {
  walletId: (wallet: AdminWallet) => wallet.walletId,
  safeAddress: (wallet: AdminWallet) => wallet.safeAddress,
  chainId: (wallet: AdminWallet) => wallet.chainId,
  createdAt: (wallet: AdminWallet) => wallet.createdAt,
};

// One array drives the header cells and the DataTableBody column count.
const WALLET_COLUMNS = [
  { column: 'walletId', label: 'Wallet ID' },
  { column: 'safeAddress', label: 'Safe Address' },
  { column: 'chainId', label: 'Chain ID' },
  { column: 'createdAt', label: 'Created At' },
] as const;

// -- Styled components --

const CardHeader = styled(Box)({
  paddingLeft: 16,
  paddingRight: 16,
  paddingTop: 16,
  paddingBottom: 8,
});

const PositionsHeading = styled(Typography)({
  paddingLeft: 16,
  paddingRight: 16,
  paddingBottom: 4,
});

export function WalletsBalancesSection({ userId }: { userId: string }) {
  const {
    data: walletsData,
    isLoading: walletsLoading,
    isError: walletsError,
  } = useUserWallets(userId);
  const {
    data: balancesData,
    isLoading: balancesLoading,
    isError: balancesError,
  } = useUserBalances(userId);

  const { rows: wallets, sort } = useTableSort(
    walletsData?.wallets ?? [],
    WALLET_SORT_ACCESSORS,
    'createdAt',
    'desc',
  );

  const balances = balancesData?.balances ?? [];
  const total = balancesData?.total;

  return (
    <Stack spacing={3}>
      {/* Total Balance Summary */}
      {(balancesLoading || total) && (
        <Stack spacing={1}>
          <Typography variant="h6">Total Balance</Typography>
          <StatGrid
            columns={3}
            stats={[
              {
                id: 'total-value',
                label: 'Total Value (USD)',
                value: formatCurrencyAmount(total?.totalValueUsd, 'USD'),
                loading: balancesLoading,
              },
              {
                id: 'total-interest',
                label: 'Total Interest (USD)',
                value: formatCurrencyAmount(total?.totalInterestUsd, 'USD'),
                loading: balancesLoading,
              },
              {
                id: 'average-apy',
                label: 'Average APY',
                value: `${total?.averageApy ?? '0'}%`,
                tone: 'neutral',
                loading: balancesLoading,
              },
            ]}
          />
        </Stack>
      )}

      {/* Wallets Table */}
      <Card variant="elevated" padding="none">
          <Typography variant="h6" sx={{ px: 2, pt: 2, pb: 1 }}>
            Wallets
          </Typography>
          <TableContainer>
            <Table sx={{ minWidth: 720 }}>
              <TableHead>
                <TableRow>
                  {WALLET_COLUMNS.map(({ column, label }) => (
                    <SortableTableCell key={column} column={column} sort={sort}>
                      {label}
                    </SortableTableCell>
                  ))}
                </TableRow>
              </TableHead>
              <DataTableBody
                columns={WALLET_COLUMNS.length}
                isLoading={walletsLoading}
                isError={walletsError}
                isEmpty={wallets.length === 0}
                emptyMessage="No wallets found."
                errorMessage="Could not load this user's wallets."
              >
                {wallets.map((w, i) => (
                  <TableRow key={w.walletId ?? i}>
                    <MonoCell>{w.walletId ?? '—'}</MonoCell>
                    <TableCell>
                      {w.safeAddress ? (
                        <CopyableId value={w.safeAddress} truncate label="Copy address" />
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusPill label={String(w.chainId ?? '')} tone="info" size="sm" />
                    </TableCell>
                    <TableCell>
                      {w.createdAt ? formatDate(w.createdAt, { month: 'short' }) : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </DataTableBody>
            </Table>
          </TableContainer>
      </Card>

      {/* Asset Balances. A failed fetch says so: it used to read as "no
          balances", which is a different fact about a money account. */}
      {balancesError && (
        <WarningCard tone="error">Could not load this user's balances.</WarningCard>
      )}

      {balancesLoading && (
        <Card variant="elevated">
            <Skeleton height={140} />
        </Card>
      )}

      {balances.map((asset, ai) => (
        <Card key={asset.assetSymbol ?? ai} variant="elevated" padding="none">
            <CardHeader>
              <Typography variant="h6">{asset.assetSymbol ?? 'Unknown Asset'}</Typography>
              <Stack direction="row" spacing={3} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                <DetailRow layout="stacked" label="Total Balance" value={asset.totalBalance ?? '0'} bold />
                <DetailRow layout="stacked" label="Total Deposited" value={asset.totalDeposited ?? '0'} />
                <DetailRow layout="stacked" label="Total Withdrawn" value={asset.totalWithdrawn ?? '0'} />
                <DetailRow
                  layout="stacked"
                  label="Daily Earning Rate"
                  value={asset.dailyEarningRate ?? '0'}
                />
                {asset.interest && (
                  <>
                    <DetailRow
                      layout="stacked"
                      label="Current Interest"
                      value={asset.interest.current ?? '0'}
                    />
                    <DetailRow
                      layout="stacked"
                      label="Lifetime Interest"
                      value={asset.interest.lifetime ?? '0'}
                    />
                  </>
                )}
              </Stack>
            </CardHeader>

            {/* Positions sub-table */}
            {asset.positions && asset.positions.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <PositionsHeading variant="subtitle2">Positions</PositionsHeading>
                <TableContainer>
                  <Table size="small" sx={{ minWidth: 1100 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Chain</TableCell>
                        <TableCell>Protocol</TableCell>
                        <TableCell>Vault Name</TableCell>
                        <TableCell>Vault Address</TableCell>
                        <TableCell>Balance</TableCell>
                        <TableCell>Share Balance</TableCell>
                        <TableCell>Base APY</TableCell>
                        <TableCell>Rewards APR</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {asset.positions.map((pos, pi) => (
                        <TableRow key={pi}>
                          <TableCell>
                            <StatusPill label={pos.chain ?? '—'} tone="info" size="sm" />
                          </TableCell>
                          <TableCell>{pos.protocol ?? '—'}</TableCell>
                          <TableCell>{pos.vaultName ?? '—'}</TableCell>
                          <MonoCell>
                            {pos.vaultAddress ? truncateMiddle(pos.vaultAddress) : '—'}
                          </MonoCell>
                          <TableCell>{pos.balance ?? '0'}</TableCell>
                          <TableCell>{pos.shareBalance ?? '0'}</TableCell>
                          <TableCell>{pos.baseApy ?? '0'}%</TableCell>
                          <TableCell>{pos.rewardsApr ?? '0'}%</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
        </Card>
      ))}

      {!balancesLoading && !balancesError && balances.length === 0 && (
        <EmptyState icon="wallet" size="sm" title="No balances for this user" />
      )}
    </Stack>
  );
}
